import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import CodeIcon from "@mui/icons-material/Code";
import HistoryIcon from "@mui/icons-material/History";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import TerminalIcon from "@mui/icons-material/Terminal";
import useJumboTheme from "@jumbo/hooks/useJumboTheme";
import useMetadata from "../../../hooks/useMetadata";
import EditorControls from "./EditorControls";
import SubmissionPanel from "./SubmissionPanel";
import MobileCodeEditorAndOutputPanel from "components/CodeEditorAndOutput/MobileCodeEditorAndOutputPanel"; // Updated import
import compilerServices from "app/services/compiler-service";
import codeConstants from "app/utils/constants/codeConstants";
import { useLocation } from "react-router-dom";
import {
  SUBMISSION_STATUSES,
  LOADING_MESSAGES,
} from "app/utils/constants/constants";
import { debounce } from "lodash";
import TestOutputContent from "components/CodeEditorAndOutput/TestOutputContent";

const MobileCodeAndTestPanel = ({
  problemData,
  selectedSubmission,
  setSelectedSubmission,
  showSubmissionPanel,
  setShowSubmissionPanel,
  codeTabValue,
  setCodeTabValue,
}) => {
  const { theme } = useJumboTheme();
  const location = useLocation();
  const {
    metadata,
    loading: metadataLoading,
    error: metadataError,
  } = useMetadata();
  const [isRunning, setIsRunning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [code, setCode] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [editorLanguage, setEditorLanguage] = useState("");
  const [output, setOutput] = useState("");
  const [testResults, setTestResults] = useState(null);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [testCaseTabValue, setTestCaseTabValue] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
  const [ioTabValue, setIOTabValue] = useState(0);
  const [hasRetrievedFromLocalStorage, setHasRetrievedFromLocalStorage] =
    useState(false);

  const pollingRef = useRef(null);

  const problemStorageKey = `problem:${location.pathname}`;

  useEffect(() => {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith("problem:") && key !== problemStorageKey) {
        localStorage.removeItem(key);
      }
    });
  }, [problemStorageKey]);

  const activeTemplates = useMemo(() => {
    const templates =
      problemData?.problemSubmissionTemplates?.filter((t) => t.active) || [];
    return templates;
  }, [problemData]);

  const availableLanguages = useMemo(() => {
    const langs =
      metadata?.programmingLanguages?.filter((lang) =>
        activeTemplates.some((t) => t.programmingLanguage === lang.key)
      ) || [];
    return langs;
  }, [metadata, activeTemplates]);

  const clearLocalStorageData = useCallback(() => {
    localStorage.removeItem(problemStorageKey);
  }, [problemStorageKey]);

  const saveCodeToLocalStorage = useMemo(
    () =>
      debounce((newCode, lang) => {
        if (lang && lang.key) {
          const existingData = localStorage.getItem(problemStorageKey)
            ? JSON.parse(localStorage.getItem(problemStorageKey))
            : {};
          existingData[lang.key] = {
            language: lang,
            code: newCode,
          };
          localStorage.setItem(problemStorageKey, JSON.stringify(existingData));
        }
      }, 500),
    [problemStorageKey]
  );

  const handleCodeChange = useCallback(
    (newCode) => {
      setCode(newCode);
      if (selectedLanguage && selectedLanguage.key) {
        saveCodeToLocalStorage(newCode, selectedLanguage);
      }
    },
    [selectedLanguage, saveCodeToLocalStorage]
  );

  useEffect(() => {
    if (hasRetrievedFromLocalStorage || selectedLanguage) {
      return;
    }

    let retrievedCode = false;
    let retrievedLanguage = false;

    if (availableLanguages.length > 0) {
      const savedData = localStorage.getItem(problemStorageKey);

      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          const matchingLanguageKey = Object.keys(parsedData).find((key) =>
            availableLanguages.some((lang) => lang.key === key)
          );

          if (matchingLanguageKey) {
            const matchingLanguageData = parsedData[matchingLanguageKey];
            const matchingLanguage = availableLanguages.find(
              (lang) => lang.key === matchingLanguageData.language.key
            );

            if (matchingLanguage) {
              setSelectedLanguage(matchingLanguage);
              setEditorLanguage(
                codeConstants[matchingLanguage.key]?.language || ""
              );
              setCode(matchingLanguageData.code || "");
              retrievedCode = true;
              retrievedLanguage = true;
            }
          }
        } catch (error) {
          console.error("Error parsing saved data from LocalStorage:", error);
        }
      }
    }

    if (
      !retrievedCode &&
      !retrievedLanguage &&
      !metadataLoading &&
      activeTemplates.length > 0 &&
      availableLanguages.length > 0 &&
      !selectedLanguage
    ) {
      const preferredLang = availableLanguages[0];
      const firstTemplate = activeTemplates.find(
        (t) => t.programmingLanguage === preferredLang.key
      );
      if (firstTemplate && preferredLang) {
        setSelectedLanguage(preferredLang);
        setCode(firstTemplate.template || "");
        setEditorLanguage(
          codeConstants[firstTemplate.programmingLanguage]?.language || ""
        );
      }
    }

    if (retrievedCode || retrievedLanguage) {
      setHasRetrievedFromLocalStorage(true);
    }
  }, [
    availableLanguages,
    problemStorageKey,
    metadataLoading,
    activeTemplates,
    selectedLanguage,
    hasRetrievedFromLocalStorage,
  ]);

  const prevShowSubmissionPanel = useRef(showSubmissionPanel);

  useEffect(() => {
    if (
      showSubmissionPanel &&
      !prevShowSubmissionPanel.current &&
      codeTabValue !== 1
    ) {
      setCodeTabValue(1);
    }
    prevShowSubmissionPanel.current = showSubmissionPanel;
  }, [showSubmissionPanel, codeTabValue, setCodeTabValue]);

  const handleCodeRun = async () => {
    setIsRunning(true);
    setOutput("");
    setTestResults(null);
    const compilerRequest = {
      sourceCode: code,
      programmingLanguage: selectedLanguage?.key,
      problemName: problemData?.name,
      executeParallely: true,
      finalSubmission: false,
      problemTests: problemData?.problemTests,
    };

    try {
      const response = await compilerServices.evaluateCode(compilerRequest);
      const { uniqueToken } = response;
      setIsPolling(true);
      pollRunStatus(uniqueToken);
    } catch (error) {
      console.error("Code evaluation error:", error);
      setOutput(`Error: ${error.message}`);
      setIsPolling(false);
      setIsRunning(false);
    }
  };

  const pollRunStatus = async (submissionId) => {
    const checkStatus = async () => {
      try {
        const result = await compilerServices.getSubmissionResultById(
          submissionId
        );
        setTestResults(result);
        if (
          result.status === SUBMISSION_STATUSES.PROCESSING ||
          result.status === SUBMISSION_STATUSES.IN_QUEUE
        ) {
          pollingRef.current = setTimeout(checkStatus, 1000);
        } else {
          setIsPolling(false);
          setIsRunning(false);
          setOutput("");
          clearTimeout(pollingRef.current);
        }
      } catch (error) {
        console.error("Polling error:", error);
        setOutput(`Polling Error: ${error.message}`);
        setIsPolling(false);
        setIsRunning(false);
        clearTimeout(pollingRef.current);
      }
    };
    checkStatus();
  };

  const handleCodeSubmit = async () => {
    setIsSubmitting(true);
    setOutput("Submitting code...");
    setSubmissionStatus(null);
    setSelectedSubmission(null);
    setIsPolling(true);
    setIsSubmitClicked(true);
    setShowSubmissionPanel(true);

    const compilerRequest = {
      sourceCode: code,
      programmingLanguage: selectedLanguage?.key,
      problemName: problemData?.name,
      executeParallely: true,
      finalSubmission: true,
      problemTests: [],
    };

    try {
      const response = await compilerServices.evaluateCode(compilerRequest);
      const { uniqueToken } = response;
      pollSubmissionStatus(uniqueToken);
    } catch (error) {
      console.error("Code submission error:", error);
      setOutput(`Error: ${error.message}`);
      setIsPolling(false);
      setIsSubmitting(false);
      clearLocalStorageData();
    }
  };

  const pollSubmissionStatus = async (submissionId) => {
    const checkStatus = async () => {
      try {
        const result = await compilerServices.getSubmissionResultById(
          submissionId
        );
        setTestResults(result);
        if (
          result.status !== SUBMISSION_STATUSES.PROCESSING &&
          result.status !== SUBMISSION_STATUSES.IN_QUEUE
        ) {
          setSubmissionStatus(result);
          setSelectedSubmission(result);
        } else {
          setIsPolling(false);
          setIsSubmitting(false);
          setOutput("");
          setTestResults(result);
          clearTimeout(pollingRef.current);
          if (result.status === SUBMISSION_STATUSES.ACCEPTED) {
            clearLocalStorageData();
          } else {
            pollingRef.current = setTimeout(checkStatus, 1000);
          }
        }
      } catch (error) {
        console.error("Polling error:", error);
        setOutput(`Polling Error: ${error.message}`);
        setIsPolling(false);
        setIsSubmitting(false);
        clearLocalStorageData();
        clearTimeout(pollingRef.current);
      }
    };
    checkStatus();
  };

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isPolling) return;
    const messages = LOADING_MESSAGES;
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % messages.length;
      setLoadingMessage(messages[index]);
    }, 2000);
    return () => clearInterval(interval);
  }, [isPolling]);

  const handleCopyCode = useCallback(
    ({ code: newCode, language }) => {
      if (!language || !language.key || !newCode) return;

      setSelectedLanguage(language);
      setEditorLanguage(codeConstants[language.key]?.language || "");
      setCode(newCode);
      saveCodeToLocalStorage(newCode, language);
      setHasRetrievedFromLocalStorage(true);
    },
    [saveCodeToLocalStorage]
  );

  const handleSwitchToCodeTab = useCallback(() => {
    setCodeTabValue(0);
  }, [setCodeTabValue]);

  const codeTabs = useMemo(() => {
    const tabs = [
      { icon: <CodeIcon />, label: "Code", content: null },
      ...(isSubmitClicked || showSubmissionPanel
        ? [
            {
              icon: <HistoryIcon />,
              label: "Submission",
              content: (
                <SubmissionPanel
                  isPolling={isPolling}
                  submissionStatus={submissionStatus}
                  theme={theme}
                  codeConstants={codeConstants}
                  metadata={metadata}
                  selectedSubmission={selectedSubmission}
                  onCopyCode={handleCopyCode}
                  onSwitchToCodeTab={handleSwitchToCodeTab}
                />
              ),
            },
          ]
        : []),
    ];
    return tabs;
  }, [
    isSubmitClicked,
    showSubmissionPanel,
    isPolling,
    submissionStatus,
    theme,
    metadata,
    selectedSubmission,
    handleCopyCode,
    handleSwitchToCodeTab,
  ]);

  const outputTabs = [
    { icon: <AssignmentTurnedInIcon />, label: "Test Cases" },
    { icon: <TerminalIcon />, label: "Test Result" },
  ];

  const outputContent = TestOutputContent({
    problemData,
    testResults,
    isPolling,
    output,
    loadingMessage,
    testCaseTabValue,
    setTestCaseTabValue,
    theme,
    showOutputs: false,
    submissionStatus,
    metadata,
  });

  const handleLanguageChange = (event) => {
    const lang = availableLanguages.find(
      (l) => l.key === event.target.value.key
    );
    if (lang && lang.key !== selectedLanguage?.key) {
      setSelectedLanguage(lang);

      const savedData = localStorage.getItem(problemStorageKey);
      let newCode = "";
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          if (parsedData[lang.key]) {
            newCode = parsedData[lang.key].code || "";
          }
        } catch (error) {
          console.error("Error parsing saved data from LocalStorage:", error);
        }
      }

      if (!newCode) {
        const template = activeTemplates.find(
          (t) => t.programmingLanguage === lang.key
        )?.template;
        newCode = template || "";
      }
      setCode(newCode);
      setEditorLanguage(codeConstants[lang.key]?.language || "");
      setHasRetrievedFromLocalStorage(true);
      saveCodeToLocalStorage(newCode, lang);
    }
  };

  const handleReset = () => {
    const template = activeTemplates.find(
      (t) => t.programmingLanguage === selectedLanguage?.key
    )?.template;
    setCode(template || "");
    setOutput("");
    setTestResults(null);
    setSubmissionStatus(null);
    setIsSubmitClicked(false);
    setShowSubmissionPanel(false);
    setSelectedSubmission(null);
    setIsSubmitting(false);
    clearLocalStorageData();
  };

  const headerControls = (
    <EditorControls
      selectedLanguage={selectedLanguage}
      onLanguageChange={handleLanguageChange}
      onRun={handleCodeRun}
      onSubmit={handleCodeSubmit}
      onReset={handleReset}
      isRunning={isRunning}
      isSubmitting={isSubmitting}
      hasActiveTemplates={activeTemplates.length > 0}
      metadataLoading={metadataLoading}
      metadataError={metadataError}
      availableLanguages={availableLanguages}
      codeConstants={codeConstants}
      setOutputTabValue={setIOTabValue}
      setCodeTabValue={setCodeTabValue}
      codeTabValue={codeTabValue}
      problemData={problemData}
    />
  );

  return (
    <MobileCodeEditorAndOutputPanel
      code={code}
      setCode={handleCodeChange}
      editorLanguage={editorLanguage}
      headerControls={headerControls}
      codeTabs={codeTabs}
      outputTabs={outputTabs}
      outputContent={outputContent}
      theme={theme}
      codeTabValue={codeTabValue}
      setCodeTabValue={setCodeTabValue}
      ioTabValue={ioTabValue}
      handleIOTabChange={(event, newValue) => setIOTabValue(newValue)}
    />
  );
};

export default MobileCodeAndTestPanel;
